import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

const ADMIN_TOKEN_KEY = 'weddingly_admin_token';
const ADMIN_USER_KEY = 'weddingly_admin_user';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private apiUrl = `${environment.apiUrl}/admin`;
  private adminUser$ = new BehaviorSubject<any>(this.getStoredAdmin());

  constructor(private http: HttpClient) {}

  private headers(): HttpHeaders {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY) || '';
    return new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': token });
  }

  // ─── Auth ──────────────────────────────────────────────────────────
  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/login`, { email, password }).pipe(
      tap(res => {
        if (res.status === 'success') {
          localStorage.setItem(ADMIN_TOKEN_KEY, res.data.token);
          localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(res.data.admin));
          this.adminUser$.next(res.data.admin);
        }
      })
    );
  }

  logout(): void {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    this.adminUser$.next(null);
  }

  isLoggedIn(): boolean { return !!localStorage.getItem(ADMIN_TOKEN_KEY); }

  getStoredAdmin(): any {
    try { return JSON.parse(localStorage.getItem(ADMIN_USER_KEY) || 'null'); }
    catch { return null; }
  }

  getAdminUser$() { return this.adminUser$.asObservable(); }

  // ─── Analytics ─────────────────────────────────────────────────────
  getAnalytics(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/analytics`, { headers: this.headers() });
  }

  // ─── Plans ─────────────────────────────────────────────────────────
  getPlans(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/plans`, { headers: this.headers() });
  }

  updatePlan(planId: string, updates: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/plans/${planId}`, updates, { headers: this.headers() });
  }

  getMerchantPlans(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/merchant-plans`, { headers: this.headers() });
  }

  updateMerchantPlan(planId: string, updates: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/merchant-plans/${planId}`, updates, { headers: this.headers() });
  }

  // ─── Users ─────────────────────────────────────────────────────────
  getUsers(params: any = {}): Observable<any> {
    const query = new URLSearchParams(params).toString();
    return this.http.get<any>(`${this.apiUrl}/users?${query}`, { headers: this.headers() });
  }

  getUserDetail(userId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/users/${userId}`, { headers: this.headers() });
  }

  updateUserPlan(userId: string, plan: string, expiry_days: number, reason: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/users/${userId}/plan`, { plan, expiry_days, reason }, { headers: this.headers() });
  }

  updateUserStatus(userId: string, action: 'suspend' | 'activate'): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/users/${userId}/status`, { action }, { headers: this.headers() });
  }

  resetUserCounters(userId: string, counters?: string[]): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/users/${userId}/reset-counters`, { counters }, { headers: this.headers() });
  }
  // ─── Merchant Approval ─────────────────────────────────────
getAllMerchants(){

 return this.http.get<any>(
 `${this.apiUrl}/admin/merchants`
 );

}
getPendingMerchants(): Observable<any> {
  return this.http.get<any>(
    `${this.apiUrl}/merchants/pending`,
    { headers: this.headers() }
  );
}

updateMerchantStatus(merchantId: string, status: string): Observable<any> {
  return this.http.put<any>(
    `${this.apiUrl}/merchants/${merchantId}/status`,
    { status },
    { headers: this.headers() }
  );
}

  // ─── Brokers ───────────────────────────────────────────────────────
  getBrokers(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/brokers`, { headers: this.headers() });
  }

  updateBrokerCommission(brokerId: string, commission_rate: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/brokers/${brokerId}/commission`, { commission_rate }, { headers: this.headers() });
  }

  settleBrokerPayout(brokerId: string, amount?: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/brokers/${brokerId}/settle`, { amount }, { headers: this.headers() });
  }

  updateBrokerStatus(brokerId: string, status: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/brokers/${brokerId}/status`, { status }, { headers: this.headers() });
  }

  updateBrokerSubscription(brokerId: string, plan_code: string, status: string = 'ACTIVE', duration_days?: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/brokers/${brokerId}/subscription`, { plan_code, status, duration_days }, { headers: this.headers() });
  }

  // ─── Broker Plans ──────────────────────────────────────────────────
  getBrokerPlans(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/broker-plans`, { headers: this.headers() });
  }

  createBrokerPlan(planData: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/broker-plans`, planData, { headers: this.headers() });
  }

  updateBrokerPlan(planId: string, updates: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/broker-plans/${planId}`, updates, { headers: this.headers() });
  }

  // ─── Verifications ─────────────────────────────────────────────────
  getVerificationQueue(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/verifications`, { headers: this.headers() });
  }

  approveVerification(profileId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/verifications/${profileId}/approve`, {}, { headers: this.headers() });
  }

  rejectVerification(profileId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/verifications/${profileId}/reject`, {}, { headers: this.headers() });
  }

  requestReupload(profileId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/verifications/${profileId}/request-reupload`, {}, { headers: this.headers() });
  }

  // ─── Reports ───────────────────────────────────────────────────────
  getReportedProfiles(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/reports`, { headers: this.headers() });
  }

  markReportReviewed(reportId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/reports/${reportId}/reviewed`, {}, { headers: this.headers() });
  }

  dismissReport(reportId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/reports/${reportId}/dismiss`, {}, { headers: this.headers() });
  }

  suspendUserFromReport(reportId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/reports/${reportId}/suspend`, {}, { headers: this.headers() });
  }

  banUserFromReport(reportId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/reports/${reportId}/ban`, {}, { headers: this.headers() });
  }

  restoreUserFromReport(reportId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/reports/${reportId}/restore`, {}, { headers: this.headers() });
  }

  // ─── Support Tickets (Admin) ────────────────────────────────────────────
  adminGetTickets(filters: any = {}): Observable<any> {
    const params = new URLSearchParams(filters).toString();
    return this.http.get<any>(`${this.apiUrl}/support/tickets?${params}`, { headers: this.headers() });
  }

  adminGetUnreadTicketCount(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/support/unread`, { headers: this.headers() });
  }

  adminGetTicketById(ticketId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/support/tickets/${ticketId}`, { headers: this.headers() });
  }

  adminReplyTicket(ticketId: string, message: string, is_internal_note: boolean = false): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/support/tickets/${ticketId}/reply`, { message, is_internal_note }, { headers: this.headers() });
  }

  adminUpdateTicketStatus(ticketId: string, status: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/support/tickets/${ticketId}/status`, { status }, { headers: this.headers() });
  }

  adminUpdateTicketPriority(ticketId: string, priority: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/support/tickets/${ticketId}/priority`, { priority }, { headers: this.headers() });
  }
}
